import dayjs from '~/timeInit';

/**
 * Hubby eSIM data-limited package sizes by Société program tier.
 * Keep in sync with `svc-lux-loyalty` `src/services/hubby/hubby.client.ts` `PACKAGE_SIZE_MAP`.
 */
const ESIM_DATA_PACKAGE_GB_BY_TIER: Record<'bronze' | 'silver' | 'gold' | 'platinum', string> = {
  bronze: '1GB',
  silver: '3GB',
  gold: '5GB',
  platinum: '10GB',
};

const PROGRAM_TIER_KEYS = ['bronze', 'silver', 'gold', 'platinum'] as const;

export type SocieteEsimOrderSummaryModel = {
  customerName: string;
  customerEmail: string;
  /** Full `fk_customer_id` (UUID) shown below email. */
  customerIdFull: string;
  tierBadgeLabel: string | null;
  bookingLine: string;
  departureDisplay: string;
  departureIso: string | null;
  localeDisplay: string;
  tripCountryLine: string;
  /** Société tier → Hubby package size label (e.g. `5GB`). */
  packageLine: string;
};

/** Shown in the order summary shell before an order is loaded or when data is unavailable. */
export const EMPTY_SOCIETE_ESIM_ORDER_SUMMARY: SocieteEsimOrderSummaryModel = {
  customerName: '—',
  customerEmail: '—',
  customerIdFull: '—',
  tierBadgeLabel: null,
  bookingLine: '—',
  departureDisplay: '—',
  departureIso: null,
  localeDisplay: '—',
  tripCountryLine: '—',
  packageLine: '—',
};

/** Rough locale label from selling region (order summary only). */
function inferLocaleFromOrder(order: App.Order): string {
  const r = order.region_code?.toUpperCase();
  if (!r) {
    return '—';
  }
  if (r === 'GB') {
    return 'en-GB';
  }
  if (r === 'IE') {
    return 'en-IE';
  }
  if (r === 'DE') {
    return 'de-DE';
  }
  if (r === 'FR') {
    return 'fr-FR';
  }
  if (['AU', 'US', 'NZ', 'CA', 'SG', 'ZA'].includes(r)) {
    return `en-${r}`;
  }
  return r;
}

/** Resolves bronze | silver | gold | platinum for Hubby package mapping (not Lux Plus subscriber tier). */
function resolveSocieteProgramTierKey(
  loyaltyAccountTier: string | null | undefined,
  order: App.Order,
): (typeof PROGRAM_TIER_KEYS)[number] | null {
  const raw = (loyaltyAccountTier || order.lux_loyalty_tier || '').trim();
  if (!raw) {
    return null;
  }
  const k = raw.toLowerCase().replace(/-/g, '_');
  if ((PROGRAM_TIER_KEYS as ReadonlyArray<string>).includes(k)) {
    return k as (typeof PROGRAM_TIER_KEYS)[number];
  }
  for (const t of PROGRAM_TIER_KEYS) {
    if (k === t || k.endsWith(`_${t}`) || k.startsWith(`${t}_`) || k.includes(`_${t}_`)) {
      return t;
    }
  }
  const last = k.split('_').filter(Boolean).pop();
  if (last && (PROGRAM_TIER_KEYS as ReadonlyArray<string>).includes(last)) {
    return last as (typeof PROGRAM_TIER_KEYS)[number];
  }
  return null;
}

function buildEsimPackageLineFromTier(loyaltyAccountTier: string | null | undefined, order: App.Order): string {
  const key = resolveSocieteProgramTierKey(loyaltyAccountTier, order);
  if (!key) {
    return '—';
  }
  const gb = ESIM_DATA_PACKAGE_GB_BY_TIER[key];
  return gb ?? '—';
}

function formatTierBadge(order: App.Order, loyaltyAccountTier?: string | null): string | null {
  const tier = (loyaltyAccountTier || order.lux_loyalty_tier || order.subscriber_tier || '').trim();
  if (!tier) {
    return null;
  }
  const normalised = tier.replace(/_/g, '').toUpperCase();
  if (normalised.includes('LUXPLUS') || normalised === 'LUXPLUS') {
    return 'LUXPLUS+';
  }
  if (normalised.includes('SOCIETE')) {
    return tier.replace(/_/g, ' ').toUpperCase();
  }
  return tier.replace(/_/g, ' ').toUpperCase();
}

type TravelExtract = {
  city: string;
  title: string;
  checkInRaw: string | null;
  countryName: string;
  countryCode: string;
};

function firstHotelLikeLine(order: App.Order): any | null {
  const fromAcc = order.accommodation_items?.[0];
  if (fromAcc) {
    return fromAcc;
  }
  const items = order.items || [];
  return (
    items.find((i: { offer_package?: { property?: unknown } }) => i.offer_package?.property) ||
    items.find(
      (i: { reservation?: { check_in?: string; start_date?: string } }) =>
        i.reservation?.check_in || i.reservation?.start_date,
    ) ||
    null
  );
}

function travelFromAccommodation(order: App.Order): TravelExtract | null {
  const item = firstHotelLikeLine(order);
  if (!item) {
    return null;
  }
  const property = item.offer_package?.property as Record<string, unknown> | undefined;
  const geo = (property?.geoData ?? property?.geo_data) as Record<string, unknown> | undefined;
  const city =
    (property?.city as string | undefined) ||
    (property?.location as string | undefined) ||
    (property?.region as string | undefined) ||
    '';
  const title = (property?.name as string | undefined) || item.name || 'Accommodation';
  const checkInRaw = item.reservation?.check_in || item.reservation?.start_date || null;
  const addr = property?.address as Record<string, unknown> | undefined;
  const countryName =
    (property?.country as string | undefined) ||
    (property?.country_name as string | undefined) ||
    (geo?.country as string | undefined) ||
    (property?.locationSubheading as string | undefined) ||
    (property?.location_subheading as string | undefined) ||
    (addr?.countryName as string | undefined) ||
    (addr?.country as string | undefined) ||
    '';
  const countryCode =
    (property?.country_code as string | undefined) ||
    (property?.country_short as string | undefined) ||
    (addr?.countryCode as string | undefined) ||
    (addr?.country_code as string | undefined) ||
    '';
  return { city, title, checkInRaw, countryName, countryCode };
}

function travelFromBedbank(order: App.Order): TravelExtract | null {
  const item = order.bedbank_items?.[0];
  if (!item) {
    return null;
  }
  const offer = item.offer;
  const city = offer?.location?.city || offer?.property?.city || '';
  const title = offer?.name || 'Bedbank stay';
  const checkInRaw = item.check_in || null;
  const countryName = offer?.property?.country || offer?.location?.country || '';
  const countryCode = offer?.property?.country_code || offer?.location?.country_code || '';
  return { city, title, checkInRaw, countryName, countryCode };
}

function travelFromTour(order: App.Order): TravelExtract | null {
  const item = order.tour_items?.[0];
  if (!item) {
    return null;
  }
  return {
    city: '',
    title: item.tour_name || 'Tour',
    checkInRaw: item.start_date ? dayjs(item.start_date).toISOString() : null,
    countryName: '',
    countryCode: '',
  };
}

function travelFromCruise(order: App.Order): TravelExtract | null {
  const item = order.cruise_items?.[0];
  if (!item) {
    return null;
  }
  return {
    city: '',
    title: item.offer_name || 'Cruise',
    checkInRaw: item.departure_date || null,
    countryName: '',
    countryCode: '',
  };
}

function travelFromExperience(order: App.Order): TravelExtract | null {
  const item = order.experience_items?.[0];
  if (!item) {
    return null;
  }
  return {
    city: '',
    title: item.title || 'Experience',
    checkInRaw: item.booking_start_date ? dayjs(item.booking_start_date).toISOString() : null,
    countryName: '',
    countryCode: '',
  };
}

function pickTravel(order: App.Order): TravelExtract | null {
  return (
    travelFromAccommodation(order) ||
    travelFromBedbank(order) ||
    travelFromTour(order) ||
    travelFromCruise(order) ||
    travelFromExperience(order)
  );
}

function joinBookingLine(t: TravelExtract): string {
  const parts: Array<string> = [];
  if (t.city) {
    parts.push(t.city);
  }
  if (t.title) {
    parts.push(t.title);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function tripCountryLineFromTravel(t: TravelExtract): string {
  if (t.countryName && t.countryCode) {
    return `${t.countryName} · ${t.countryCode}`;
  }
  if (t.countryName) {
    return t.countryName;
  }
  if (t.countryCode) {
    return t.countryCode;
  }
  return '—';
}

/** Common ISO 3166-1 alpha-3 → alpha-2 so selling-region filtering matches flight payloads. */
const ISO3166_ALPHA3_TO_ALPHA2: Record<string, string> = {
  ABW: 'AW',
  AND: 'AD',
  ARE: 'AE',
  ARG: 'AR',
  ATG: 'AG',
  AUS: 'AU',
  AUT: 'AT',
  BEL: 'BE',
  BGR: 'BG',
  BHR: 'BH',
  BHS: 'BS',
  BLR: 'BY',
  BOL: 'BO',
  BRA: 'BR',
  BRB: 'BB',
  BRN: 'BN',
  BTN: 'BT',
  CAN: 'CA',
  CHE: 'CH',
  CHL: 'CL',
  CHN: 'CN',
  COL: 'CO',
  CRI: 'CR',
  CUB: 'CU',
  CYP: 'CY',
  CZE: 'CZ',
  DEU: 'DE',
  DNK: 'DK',
  DOM: 'DO',
  ECU: 'EC',
  EGY: 'EG',
  ESP: 'ES',
  EST: 'EE',
  ETH: 'ET',
  FIN: 'FI',
  FJI: 'FJ',
  FRA: 'FR',
  GBR: 'GB',
  GEO: 'GE',
  GHA: 'GH',
  GRC: 'GR',
  GTM: 'GT',
  HKG: 'HK',
  HND: 'HN',
  HRV: 'HR',
  HUN: 'HU',
  IDN: 'ID',
  IND: 'IN',
  IRL: 'IE',
  IRN: 'IR',
  IRQ: 'IQ',
  ISL: 'IS',
  ISR: 'IL',
  ITA: 'IT',
  JAM: 'JM',
  JOR: 'JO',
  JPN: 'JP',
  KAZ: 'KZ',
  KEN: 'KE',
  KHM: 'KH',
  KOR: 'KR',
  KWT: 'KW',
  LAO: 'LA',
  LBN: 'LB',
  LKA: 'LK',
  LTU: 'LT',
  LUX: 'LU',
  LVA: 'LV',
  MAR: 'MA',
  MCO: 'MC',
  MDA: 'MD',
  MDG: 'MG',
  MEX: 'MX',
  MLT: 'MT',
  MMR: 'MM',
  MNE: 'ME',
  MNG: 'MN',
  MYS: 'MY',
  NAM: 'NA',
  NGA: 'NG',
  NIC: 'NI',
  NLD: 'NL',
  NOR: 'NO',
  NPL: 'NP',
  NRU: 'NR',
  NZL: 'NZ',
  OMN: 'OM',
  PAK: 'PK',
  PAN: 'PA',
  PER: 'PE',
  PHL: 'PH',
  PNG: 'PG',
  POL: 'PL',
  PRI: 'PR',
  PRT: 'PT',
  PRY: 'PY',
  QAT: 'QA',
  ROU: 'RO',
  RUS: 'RU',
  SAU: 'SA',
  SEN: 'SN',
  SGP: 'SG',
  SLB: 'SB',
  SLV: 'SV',
  SMR: 'SM',
  SRB: 'RS',
  SVK: 'SK',
  SVN: 'SI',
  SWE: 'SE',
  SYC: 'SC',
  SYR: 'SY',
  THA: 'TH',
  TJK: 'TJ',
  TKM: 'TM',
  TLS: 'TL',
  TON: 'TO',
  TTO: 'TT',
  TUN: 'TN',
  TUR: 'TR',
  TWN: 'TW',
  TZA: 'TZ',
  UGA: 'UG',
  UKR: 'UA',
  URY: 'UY',
  USA: 'US',
  UZB: 'UZ',
  VCT: 'VC',
  VEN: 'VE',
  VNM: 'VN',
  VUT: 'VU',
  WSM: 'WS',
  ZAF: 'ZA',
  ZMB: 'ZM',
  ZWE: 'ZW',
};

/** Lowercase English country name → ISO 3166-1 alpha-2 (flight / property payloads often use full names). */
const ENGLISH_COUNTRY_NAME_TO_ISO2: Record<string, string> = {
  andorra: 'AD',
  'united arab emirates': 'AE',
  afghanistan: 'AF',
  'antigua and barbuda': 'AG',
  anguilla: 'AI',
  albania: 'AL',
  armenia: 'AM',
  angola: 'AO',
  argentina: 'AR',
  'american samoa': 'AS',
  austria: 'AT',
  australia: 'AU',
  aruba: 'AW',
  'åland islands': 'AX',
  azerbaijan: 'AZ',
  'bosnia and herzegovina': 'BA',
  barbados: 'BB',
  bangladesh: 'BD',
  belgium: 'BE',
  'burkina faso': 'BF',
  bulgaria: 'BG',
  bahrain: 'BH',
  burundi: 'BI',
  benin: 'BJ',
  bermuda: 'BM',
  brunei: 'BN',
  bolivia: 'BO',
  'bonaire, sint eustatius and saba': 'BQ',
  brazil: 'BR',
  bahamas: 'BS',
  bhutan: 'BT',
  botswana: 'BW',
  belarus: 'BY',
  belize: 'BZ',
  canada: 'CA',
  'cocos (keeling) islands': 'CC',
  'democratic republic of the congo': 'CD',
  'central african republic': 'CF',
  congo: 'CG',
  switzerland: 'CH',
  "côte d'ivoire": 'CI',
  "cote d'ivoire": 'CI',
  'cook islands': 'CK',
  chile: 'CL',
  cameroon: 'CM',
  china: 'CN',
  colombia: 'CO',
  'costa rica': 'CR',
  cuba: 'CU',
  'cape verde': 'CV',
  curaçao: 'CW',
  cyprus: 'CY',
  'czech republic': 'CZ',
  czechia: 'CZ',
  germany: 'DE',
  djibouti: 'DJ',
  denmark: 'DK',
  dominica: 'DM',
  'dominican republic': 'DO',
  algeria: 'DZ',
  ecuador: 'EC',
  estonia: 'EE',
  egypt: 'EG',
  'western sahara': 'EH',
  eritrea: 'ER',
  spain: 'ES',
  ethiopia: 'ET',
  finland: 'FI',
  fiji: 'FJ',
  'falkland islands': 'FK',
  micronesia: 'FM',
  'faroe islands': 'FO',
  france: 'FR',
  gabon: 'GA',
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  grenada: 'GD',
  georgia: 'GE',
  'french guiana': 'GF',
  guernsey: 'GG',
  ghana: 'GH',
  gibraltar: 'GI',
  greenland: 'GL',
  gambia: 'GM',
  guinea: 'GN',
  guadeloupe: 'GP',
  'equatorial guinea': 'GQ',
  greece: 'GR',
  'south georgia and the south sandwich islands': 'GS',
  guatemala: 'GT',
  guam: 'GU',
  'guinea-bissau': 'GW',
  guyana: 'GY',
  'hong kong': 'HK',
  honduras: 'HN',
  croatia: 'HR',
  haiti: 'HT',
  hungary: 'HU',
  indonesia: 'ID',
  bali: 'ID',
  denpasar: 'ID',
  lombok: 'ID',
  ireland: 'IE',
  israel: 'IL',
  'isle of man': 'IM',
  india: 'IN',
  'british indian ocean territory': 'IO',
  iraq: 'IQ',
  iran: 'IR',
  iceland: 'IS',
  italy: 'IT',
  jersey: 'JE',
  jamaica: 'JM',
  jordan: 'JO',
  japan: 'JP',
  kenya: 'KE',
  kyrgyzstan: 'KG',
  cambodia: 'KH',
  kiribati: 'KI',
  comoros: 'KM',
  'saint kitts and nevis': 'KN',
  'north korea': 'KP',
  'south korea': 'KR',
  korea: 'KR',
  kuwait: 'KW',
  'cayman islands': 'KY',
  kazakhstan: 'KZ',
  laos: 'LA',
  lebanon: 'LB',
  'saint lucia': 'LC',
  liechtenstein: 'LI',
  'sri lanka': 'LK',
  liberia: 'LR',
  lesotho: 'LS',
  lithuania: 'LT',
  luxembourg: 'LU',
  latvia: 'LV',
  libya: 'LY',
  morocco: 'MA',
  monaco: 'MC',
  moldova: 'MD',
  montenegro: 'ME',
  madagascar: 'MG',
  'marshall islands': 'MH',
  macedonia: 'MK',
  'north macedonia': 'MK',
  mali: 'ML',
  myanmar: 'MM',
  mongolia: 'MN',
  macau: 'MO',
  'northern mariana islands': 'MP',
  martinique: 'MQ',
  mauritania: 'MR',
  montserrat: 'MS',
  malta: 'MT',
  mauritius: 'MU',
  maldives: 'MV',
  malawi: 'MW',
  mexico: 'MX',
  malaysia: 'MY',
  mozambique: 'MZ',
  namibia: 'NA',
  'new caledonia': 'NC',
  niger: 'NE',
  norfolk: 'NF',
  nigeria: 'NG',
  nicaragua: 'NI',
  netherlands: 'NL',
  holland: 'NL',
  norway: 'NO',
  nepal: 'NP',
  nauru: 'NR',
  niue: 'NU',
  'new zealand': 'NZ',
  oman: 'OM',
  panama: 'PA',
  peru: 'PE',
  'french polynesia': 'PF',
  'papua new guinea': 'PG',
  philippines: 'PH',
  pakistan: 'PK',
  poland: 'PL',
  'saint pierre and miquelon': 'PM',
  'puerto rico': 'PR',
  portugal: 'PT',
  palau: 'PW',
  paraguay: 'PY',
  qatar: 'QA',
  réunion: 'RE',
  reunion: 'RE',
  romania: 'RO',
  serbia: 'RS',
  russia: 'RU',
  rwanda: 'RW',
  'saudi arabia': 'SA',
  'solomon islands': 'SB',
  seychelles: 'SC',
  sudan: 'SD',
  sweden: 'SE',
  singapore: 'SG',
  'saint helena': 'SH',
  slovenia: 'SI',
  slovakia: 'SK',
  'sierra leone': 'SL',
  'san marino': 'SM',
  senegal: 'SN',
  somalia: 'SO',
  suriname: 'SR',
  'south sudan': 'SS',
  'são tomé and príncipe': 'ST',
  'el salvador': 'SV',
  'sint maarten': 'SX',
  syria: 'SY',
  swaziland: 'SZ',
  eswatini: 'SZ',
  'turks and caicos islands': 'TC',
  chad: 'TD',
  'french southern territories': 'TF',
  togo: 'TG',
  thailand: 'TH',
  tajikistan: 'TJ',
  tokelau: 'TK',
  'timor-leste': 'TL',
  turkmenistan: 'TM',
  tunisia: 'TN',
  tonga: 'TO',
  turkey: 'TR',
  'trinidad and tobago': 'TT',
  tuvalu: 'TV',
  taiwan: 'TW',
  tanzania: 'TZ',
  ukraine: 'UA',
  uganda: 'UG',
  'united states': 'US',
  'united states of america': 'US',
  usa: 'US',
  uruguay: 'UY',
  uzbekistan: 'UZ',
  'holy see': 'VA',
  'vatican city': 'VA',
  'saint vincent and the grenadines': 'VC',
  venezuela: 'VE',
  'british virgin islands': 'VG',
  'u.s. virgin islands': 'VI',
  vietnam: 'VN',
  vanuatu: 'VU',
  'wallis and futuna': 'WF',
  samoa: 'WS',
  kosovo: 'XK',
  yemen: 'YE',
  mayotte: 'YT',
  'south africa': 'ZA',
  zambia: 'ZM',
  zimbabwe: 'ZW',
};

function normalizeCountryHint(raw: unknown, depth = 0): string | null {
  if (raw == null || depth > 3) {
    return null;
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    return (
      normalizeCountryHint(o.iso2, depth + 1) ??
      normalizeCountryHint(o.alpha2, depth + 1) ??
      normalizeCountryHint(o.code, depth + 1) ??
      normalizeCountryHint(o.countryCode, depth + 1) ??
      normalizeCountryHint(o.country_code, depth + 1) ??
      normalizeCountryHint(o.shortCode, depth + 1) ??
      normalizeCountryHint(o.country, depth + 1) ??
      normalizeCountryHint(o.name, depth + 1)
    );
  }
  const s = String(raw).trim();
  if (!s || s === '[object Object]') {
    return null;
  }
  if (/^[a-z]{2}$/i.test(s)) {
    return s.toUpperCase();
  }
  if (/^[a-z]{3}$/i.test(s)) {
    // Drop unmapped alpha-3 codes; the destination set must contain ISO-2 only.
    return ISO3166_ALPHA3_TO_ALPHA2[s.toUpperCase()] ?? null;
  }
  const fromName = ENGLISH_COUNTRY_NAME_TO_ISO2[s.toLowerCase()];
  return fromName || null;
}

function addCountryCode(set: Set<string>, raw: unknown): void {
  const code = normalizeCountryHint(raw);
  if (code) {
    set.add(code);
  }
}

/**
 * ISO2 codes that often appear on properties as the **selling region / billing**, not the hotel location
 * (e.g. Bali stay with `country_code: AU`).
 */
const PROPERTY_GATEWAY_MARKET_ISO2 = new Set([
  'AU',
  'US',
  'GB',
  'NZ',
  'CA',
  'SG',
  'IE',
  'ZA',
  'DE',
  'FR',
  'NL',
  'HK',
  'MO',
  'AE',
]);

/** Best-effort “where is this property” from geo/address/names — before flat `country_code`. */
function inferPropertyTruthCountryCode(property: unknown): string | null {
  if (!property || typeof property !== 'object') {
    return null;
  }
  const p = property as Record<string, unknown>;
  const geo = (p.geoData ?? p.geo_data) as Record<string, unknown> | undefined;
  if (geo && typeof geo === 'object') {
    for (const key of ['country', 'countryCode', 'country_code', 'shortCode'] as const) {
      const c = normalizeCountryHint(geo[key]);
      if (c) {
        return c;
      }
    }
  }
  const addr = p.address;
  if (addr && typeof addr === 'object') {
    const a = addr as Record<string, unknown>;
    for (const key of ['countryName', 'country', 'countryCode', 'country_code'] as const) {
      const c = normalizeCountryHint(a[key]);
      if (c) {
        return c;
      }
    }
  }
  for (const key of ['country_name', 'country'] as const) {
    const c = normalizeCountryHint(p[key]);
    if (c) {
      return c;
    }
  }
  for (const loc of [p.locationSubheading, p.location_subheading]) {
    if (typeof loc !== 'string') {
      continue;
    }
    const parts = loc
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      const c = normalizeCountryHint(parts[parts.length - 1]);
      if (c) {
        return c;
      }
    }
    const c = normalizeCountryHint(loc);
    if (c) {
      return c;
    }
  }
  return null;
}

function isGatewayOnlyCountrySet(codes: Set<string>): boolean {
  if (codes.size === 0) {
    return false;
  }
  for (const c of codes) {
    if (!PROPERTY_GATEWAY_MARKET_ISO2.has(c)) {
      return false;
    }
  }
  return true;
}

function hasNonGatewayCountry(codes: Set<string>): boolean {
  for (const c of codes) {
    if (!PROPERTY_GATEWAY_MARKET_ISO2.has(c)) {
      return true;
    }
  }
  return false;
}

/** Property payloads vary: geoData, flat country_*, address, location subheading (admin getOrder). */
function addCountryHintsFromProperty(set: Set<string>, property: unknown): void {
  if (!property || typeof property !== 'object') {
    return;
  }
  const p = property as Record<string, unknown>;
  const truth = inferPropertyTruthCountryCode(property);

  const geo = (p.geoData ?? p.geo_data) as Record<string, unknown> | undefined;
  if (geo && typeof geo === 'object') {
    addCountryCode(set, geo.country);
    addCountryCode(set, geo.countryCode);
    addCountryCode(set, geo.country_code);
    addCountryCode(set, geo.shortCode);
  }
  addCountryCode(set, p.country);
  addCountryCode(set, p.country_name);
  addCountryCode(set, p.locationSubheading);
  addCountryCode(set, p.location_subheading);
  const addr = p.address;
  if (addr && typeof addr === 'object') {
    const a = addr as Record<string, unknown>;
    addCountryCode(set, a.countryCode);
    addCountryCode(set, a.country_code);
    addCountryCode(set, a.countryName);
    addCountryCode(set, a.country);
  }

  const flatCode = normalizeCountryHint(p.country_code);
  const flatShort = normalizeCountryHint(p.country_short);
  const skipFlat =
    truth != null && flatCode != null && PROPERTY_GATEWAY_MARKET_ISO2.has(flatCode) && flatCode !== truth;
  if (!skipFlat) {
    if (flatCode) {
      set.add(flatCode);
    }
    if (flatShort) {
      set.add(flatShort);
    }
  }
}

function addCountryHintsFromOfferLocation(set: Set<string>, location: unknown): void {
  if (!location || typeof location !== 'object') {
    return;
  }
  const loc = location as Record<string, unknown>;
  addCountryCode(set, loc.country);
  addCountryCode(set, loc.countryCode);
  addCountryCode(set, loc.country_code);
  addCountryCode(set, loc.subheading);
  if (typeof loc.description === 'string') {
    const parts = loc.description
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      addCountryCode(set, parts[parts.length - 1]);
    }
  }
}

function collectCountriesFromOrderItems(order: App.Order, set: Set<string>): void {
  for (const item of order.items || []) {
    addCountryHintsFromProperty(set, item?.offer_package?.property);
    addCountryHintsFromProperty(set, item?.fullReservation?.property);
    const offer = item.offer as unknown as Record<string, unknown> | undefined;
    if (offer && typeof offer === 'object') {
      const loc = offer.location;
      if (loc && typeof loc === 'object') {
        addCountryHintsFromOfferLocation(set, loc);
      }
    }
  }
}

function collectFlightsFromJourneyFlight(set: Set<string>, jf: App.JourneyFlight | undefined): void {
  for (const f of jf?.flights || []) {
    addCountryCode(set, f.departing_country);
    addCountryCode(set, f.arrival_country);
  }
}

function collectFlightsFromReturningJourney(set: Set<string>, returning: unknown): void {
  const r = returning as { flights?: Array<App.Flight> } | undefined;
  for (const f of r?.flights || []) {
    addCountryCode(set, f.departing_country);
    addCountryCode(set, f.arrival_country);
  }
}

function collectCountriesFromFlightItems(order: App.Order, set: Set<string>): void {
  if (!order.flight_items?.length) {
    return;
  }
  for (const item of order.flight_items) {
    addCountryCode(set, item.departing_country);
    addCountryCode(set, item.arrival_country);
    const j = item.journey;
    if (!j) {
      continue;
    }
    collectFlightsFromJourneyFlight(set, j.departing);
    collectFlightsFromReturningJourney(set, j.returning);
    for (const leg of j.legs || []) {
      collectFlightsFromJourneyFlight(set, leg);
    }
  }
}

function sellingRegionIso2(order: App.Order): string | null {
  const r = order.region_code?.trim().toUpperCase();
  if (r && /^[A-Z]{2}$/.test(r)) {
    return r;
  }
  return null;
}

function collectPropertyDestinationCountries(order: App.Order): Set<string> {
  const set = new Set<string>();
  collectCountriesFromOrderItems(order, set);
  for (const item of order.accommodation_items || []) {
    addCountryHintsFromProperty(set, item?.offer_package?.property);
  }
  for (const item of order.bedbank_items || []) {
    const offer = item?.offer;
    addCountryHintsFromProperty(set, offer?.property);
    addCountryHintsFromOfferLocation(set, offer?.location);
  }
  return set;
}

function collectAuxiliaryDestinationCountries(order: App.Order): Set<string> {
  const set = new Set<string>();
  for (const tour of order.tour_items || []) {
    for (const c of tour.countries_visited || []) {
      addCountryCode(set, c);
    }
  }
  collectCountriesFromFlightItems(order, set);
  for (const ins of order.insurance_items || []) {
    for (const d of ins.destinations || []) {
      addCountryCode(set, d);
    }
  }
  for (const co of order.custom_offer_items || []) {
    for (const ci of co.custom_offer?.items || []) {
      addCountryCode(set, ci.country);
    }
  }
  return set;
}

/**
 * Distinct destination country codes from the booking. Prefer **property** (stay) locations;
 * if those are only selling-region gateway codes (e.g. AU) but flights/tours show real destinations,
 * use the auxiliary set. Drop selling `region_code` when other countries remain.
 */
function collectBookingDestinationCountryCodes(order: App.Order): Array<string> {
  const primary = collectPropertyDestinationCountries(order);
  const secondary = collectAuxiliaryDestinationCountries(order);

  let set: Set<string>;
  if (primary.size > 0 && isGatewayOnlyCountrySet(primary) && hasNonGatewayCountry(secondary)) {
    set = new Set(secondary);
  } else if (primary.size > 0) {
    set = primary;
  } else {
    set = new Set(secondary);
  }

  const home = sellingRegionIso2(order);
  const sorted = [...set].sort((a, b) => a.localeCompare(b));
  if (!home || sorted.length === 0) {
    return sorted;
  }
  const intl = sorted.filter((c) => c !== home);
  return intl.length > 0 ? intl : sorted;
}

function formatTripCountryLine(order: App.Order, travel: TravelExtract | null): string {
  const codes = collectBookingDestinationCountryCodes(order);
  if (codes.length > 0) {
    return codes.join(' · ');
  }
  return travel ? tripCountryLineFromTravel(travel) : '—';
}

export function buildSocieteEsimOrderSummaryModel(
  order: App.Order,
  options?: {
    loyaltyAccountTier?: string | null;
  },
): SocieteEsimOrderSummaryModel {
  const travel = pickTravel(order);
  const checkInRaw = travel?.checkInRaw ?? null;
  // Date-only strings ("2026-06-15") parse in browser tz, which can shift the calendar day
  // for admins outside Sydney. Detect and parse those as Sydney; ISO strings with offsets
  // are already unambiguous and parse the same in any tz.
  const isDateOnly = typeof checkInRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(checkInRaw);
  const departureDate = checkInRaw ? (isDateOnly ? dayjs.tz(checkInRaw, 'Australia/Sydney') : dayjs(checkInRaw)) : null;
  const departureDisplay = departureDate?.isValid() ? departureDate.tz('Australia/Sydney').format('DD/MM/YYYY') : '—';
  const departureIso = departureDate?.isValid() ? departureDate.toISOString() : null;

  const bookingLine = travel ? joinBookingLine(travel) : '—';
  const tripCountryLine = formatTripCountryLine(order, travel);

  const packageLine = buildEsimPackageLineFromTier(options?.loyaltyAccountTier ?? null, order);

  return {
    customerName: order.customer_full_name?.trim() || '—',
    customerEmail: order.customer_email?.trim() || '—',
    customerIdFull: order.fk_customer_id?.trim() || '—',
    tierBadgeLabel: formatTierBadge(order, options?.loyaltyAccountTier ?? null),
    bookingLine,
    departureDisplay,
    departureIso,
    localeDisplay: inferLocaleFromOrder(order),
    tripCountryLine,
    packageLine,
  };
}
